import { DateTime } from 'luxon';
import { useContext } from 'react';
import AppContext from '../components/context/AppContext';
import constants from '../constants.json' assert { type: 'json' };

/**
 * This function builds structured data for a product for use in rich snippets
 * @param {object} product Main product data (title, Seo description, etc.)
 * @returns A JSON stringified object that includes the product structured data
 */

const useBuildProductStructuredData = () => {
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    return (product = {}) => {
        /* Reviews */
        const productStarsRating = product.Reviews
            ?.map((review) => review.starsRating)
            .reduce((prev, cur) => +prev + +cur, 0);
        const totalReviews = product.Reviews?.length ?? 0;
        /* Description */
        const title = product.seoTitle ?? product.title;
        const description = product.seoDescription ?? '';
        /* Images */
        const images = product.Images
            ?.map((image) => image.url)
            .filter(Boolean)
            ?? [];
        /* Other data */
        const structuredDataObj = {
            '@context': 'https://schema.org/',
            '@type': 'Product',
            'name': title,
            ...description && ({ 'description': description }),
            ...product.sku && ({ 'sku': product.sku.split(' ').join('-') }),
            'brand': {
                '@type': 'Brand',
                ...product.vendor && ({ 'name': product.vendor }),
            },
            ...totalReviews > 0 && ({ 'aggregateRating': {
                '@type': 'AggregateRating',
                'ratingValue': (productStarsRating / totalReviews).toFixed(2),
                'reviewCount': totalReviews,
            } }),
            ...images.length > 0 && ({ 'image': images }),
            'offers': {
                '@type': 'Offer',
                'price': product.price,
                'priceCurrency': appConfig.storeCurrency,
                'availability': product.availableForSale ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                'priceValidUntil': DateTime.now().setZone(appContext.config.appTimeZone).plus({ weeks: 1 }).endOf('day').toISODate(),
                'hasMerchantReturnPolicy': {
                    '@type': 'MerchantReturnPolicy',
                    'returnPolicyCategory': 'https://schema.org/MerchantReturnFiniteReturnWindow',
                    'merchantReturnDays': appContext.config.merchantReturnDays || constants.DEFAULT_MERCHANT_RETURN_DAYS,
                    'returnFees': 'https://schema.org/RestockingFees',
                    'applicableCountry': 'US',
                    'returnMethod': 'https://schema.org/ReturnByMail',
                },
            }
        };
        return JSON.stringify(structuredDataObj);
    };
};

export default useBuildProductStructuredData;